// src/modules/assetManagement/management/ManageAssetPanel.tsx
// Asset Management actions for one asset, shown on the asset page when opened from Asset Management.
import React, { useRef, useState } from "react";
import { Archive, ArchiveRestore, Headphones, Link2Off, Share2, Trash2, Wrench } from "lucide-react";
import { assetApi, RESTORE_ESTIMATE } from "../assetApi";
import { ConfirmModal, ShareModal, SupportModal, UnlinkModal } from "../components/AssetModals";
import { Button } from "../components/ui";
import type { Asset } from "../types";

type Dialog = "archive" | "restore" | "delete" | "share" | "unlink" | "support" | null;

const ManageAssetPanel: React.FC<{
  asset: Asset;
  onMessage: (message: string) => void;
  onDeleted: () => void;
  onReplaced?: (newAssetId: string) => void;
}> = ({ asset, onMessage, onDeleted, onReplaced }) => {
  const [dialog, setDialog] = useState<Dialog>(null);
  const replaceInput = useRef<HTMLInputElement>(null);

  const links = asset.eventIds.length + asset.productIds.length;
  const canShare = asset.storage === "standard" && asset.status === "completed";
  const needsFix = asset.status === "failed" || asset.status === "rejected";

  const done = (message: string) => {
    setDialog(null);
    onMessage(message);
  };

  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">Manage this asset</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-4">Only possible in Asset Management</p>

      <div className="grid grid-cols-2 gap-2">
        {asset.storage === "standard" && (
          <Button size="sm" icon={<Archive size={14} />} onClick={() => setDialog("archive")}>Archive</Button>
        )}
        {asset.storage === "archived" && (
          <Button size="sm" icon={<ArchiveRestore size={14} />} onClick={() => setDialog("restore")}>Restore</Button>
        )}
        {asset.storage === "restoring" && (
          <Button size="sm" icon={<ArchiveRestore size={14} />} disabled title="Restore in progress">Restoring…</Button>
        )}
        <Button size="sm" icon={<Share2 size={14} />} disabled={!canShare} onClick={() => setDialog("share")}
          title={canShare ? "" : "Only active, ready assets can be shared"}>
          Share
        </Button>
        <Button size="sm" icon={<Link2Off size={14} />} disabled={!links} onClick={() => setDialog("unlink")}
          title={links ? "" : "Not linked to any Event or Product"}>
          Unlink
        </Button>
        <Button size="sm" icon={<Trash2 size={14} />} className="!text-red-700" onClick={() => setDialog("delete")}>
          Delete
        </Button>
        {needsFix && (
          <Button size="sm" icon={<Wrench size={14} />} className="col-span-2" onClick={() => replaceInput.current?.click()}>
            Replace file
          </Button>
        )}
        <Button size="sm" variant="ghost" icon={<Headphones size={14} />} className="col-span-2" onClick={() => setDialog("support")}>
          Get first-line support
        </Button>
      </div>

      <input
        ref={replaceInput}
        type="file"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          onMessage(`Uploading ${file.name} to replace this asset…`);
          try {
            const newId = await assetApi.replaceAssetFile(asset.id, file);
            onMessage(`Replaced with ${file.name}`);
            onReplaced?.(newId);
          } catch (err) {
            onMessage(`Couldn't replace the file: ${err instanceof Error ? err.message : "upload failed"}`);
          }
        }}
      />

      {dialog === "archive" && (
        <ConfirmModal
          title="Archive this asset?"
          confirmLabel="Archive"
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await assetApi.archiveAssets([asset.id]);
            done("Asset archived");
          }}
          body={
            <>
              <p>It moves to cold storage and keeps its links, but can't be played, previewed or shared until restored.</p>
              <p className="text-slate-500">Restoring from the archive typically takes {RESTORE_ESTIMATE}.</p>
            </>
          }
        />
      )}
      {dialog === "restore" && (
        <ConfirmModal
          title="Restore this asset?"
          confirmLabel="Start restore"
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await assetApi.restoreAssets([asset.id]);
            done("Restore started");
          }}
          body={<p>The file is brought back from cold storage. It shows as Restoring until it is ready to view and share again.</p>}
        />
      )}
      {dialog === "delete" && (
        <ConfirmModal
          title="Delete this asset?"
          confirmLabel="Delete permanently"
          tone="danger"
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await assetApi.deleteAssets([asset.id]);
            setDialog(null);
            onDeleted();
          }}
          body={
            <>
              <p>This permanently removes <span className="font-medium">{asset.filename}</span>. It can't be undone.</p>
              {links > 0 && (
                <p className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-800">
                  Its {links} link{links === 1 ? "" : "s"} to Events and Products will be removed; the Events and Products themselves stay.
                </p>
              )}
            </>
          }
        />
      )}
      {dialog === "share" && <ShareModal assets={[asset]} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "unlink" && <UnlinkModal assets={[asset]} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "support" && (
        <SupportModal asset={asset} onClose={() => setDialog(null)} onRaised={(t) => onMessage(`Support ticket ${t.ref} raised`)} />
      )}
    </section>
  );
};

export default ManageAssetPanel;
